import { api, type Package, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { formatDate, formatLds, formatUsd } from '../../lib/format';
import { Chip } from '../../ui';

const col = createColumnHelper<Package>();

export function PackageManagement() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.packages(),
    queryFn: () => api.packages.adminList().then((r) => r.data),
  });

  const toggle = useMutation({
    mutationFn: (id: string) => api.packages.toggle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.packages() }),
  });

  const columns = useMemo(
    () => [
      col.accessor('name', { header: 'Package' }),
      col.accessor('amount_usd', { header: 'Price (USD)', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: 'Price (LDS)', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('description', { header: 'Description', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('is_active', {
        header: 'Status',
        cell: (i) =>
          i.getValue() ? <Chip tone="success">Active</Chip> : <Chip tone="danger">Disabled</Chip>,
      }),
      col.accessor('created_at', { header: 'Created', cell: (i) => formatDate(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <Chip as="button" tone="gold" onClick={() => toggle.mutate(info.row.original.id)}>
            {info.row.original.is_active ? 'Disable' : 'Enable'}
          </Chip>
        ),
      }),
    ],
    [toggle],
  );

  return (
    <>
      <PageHeader
        title="Packages"
        description="Alpha One ships a single $100 joining package, payable in LDS Token."
      />
      <DataTable<Package>
        title="Packages"
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
      />
    </>
  );
}
